// Import the pure helpers from the tiptap-free *Utils module (not the extension file) so the read-page
// barrel that re-exports extractPreviewImageUrlsFromTipTapContent stays free of @tiptap/prosemirror.
// See 2026-06-11-homepage-cwv-remaining-structural-slices-design.md (Slice B / B1).
import { walkTiptapJson, type TiptapJsonNode } from "../extension/UserMentionUtils";

const YOUTUBE_VIDEO_ID_PATTERN = /(?:https?:)?\/\/(?:www\.)?(?:youtu\.be\/|youtube(?:-nocookie)?\.com\/(?:watch\?(?:[^#\s]*&)?v=|embed\/|shorts\/|live\/))([A-Za-z0-9_-]{6,})/i;

function normalizePreviewImageUrl(value: unknown): string | null {
    if (typeof value !== "string") {
        return null;
    }

    const trimmed = value.trim();
    return trimmed ? trimmed : null;
}

function escapeSvgText(value: string): string {
    return value
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&apos;");
}

// No provider label (FI-813, operator decision #4 of 2026-09-04): the data URL is computed once at parse time and
// served to every locale, so a baked-in English name could never be localized. Colours tell the providers apart.
function buildProviderPlaceholderDataUrl(accentStart: string, accentEnd: string, previewKey: string): string {
    const safePreviewKey = escapeSvgText(previewKey);
    const svg = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 630">
  <desc>${safePreviewKey}</desc>
  <defs>
    <linearGradient id="bg" x1="0" y1="0" x2="1" y2="1">
      <stop offset="0%" stop-color="${accentStart}"/>
      <stop offset="100%" stop-color="${accentEnd}"/>
    </linearGradient>
  </defs>
  <rect width="1200" height="630" rx="40" fill="url(#bg)"/>
  <rect x="72" y="72" width="1056" height="486" rx="32" fill="rgba(15,23,42,0.22)" stroke="rgba(255,255,255,0.18)" stroke-width="2"/>
  <circle cx="600" cy="250" r="82" fill="rgba(255,255,255,0.16)"/>
  <polygon points="575,205 575,295 650,250" fill="#ffffff"/>
  <text x="600" y="450" text-anchor="middle" font-family="-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif" font-size="28" fill="rgba(255,255,255,0.92)">Embedded media preview</text>
</svg>`.trim();

    return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
}

function extractYouTubeVideoId(sourceUrl: string | null): string | null {
    if (!sourceUrl) {
        return null;
    }

    const match = sourceUrl.match(YOUTUBE_VIDEO_ID_PATTERN);
    return match?.[1] ?? null;
}

function buildYouTubePreviewImageUrl(sourceUrl: string | null, previewKey: string): string {
    const videoId = extractYouTubeVideoId(sourceUrl);
    if (!videoId) {
        return buildProviderPlaceholderDataUrl("#ef4444", "#7f1d1d", previewKey);
    }

    return `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`;
}

/**
 * The preview image for ONE node, or null. {@code nodePath} is the pre-order index key
 * ({@code node-<n>}, root = 0) used when the node has no src. Exported so {@code getTipTapParseMetadata}
 * can collect previews inside its single walk (C-F13); {@link extractPreviewImageUrlsFromTipTapContent}
 * is the standalone walk over the same function.
 */
export function buildPreviewImageUrl(node: TiptapJsonNode, nodePath: string): string | null {
    const nodeType = node.type;
    const attrs = node.attrs ?? null;
    const src = normalizePreviewImageUrl(attrs && typeof attrs === "object" ? attrs.src : null);
    const previewKey = src ?? nodePath;

    switch (nodeType) {
        case "image":
            return src;
        case "linkCard":
            return normalizePreviewImageUrl(attrs && typeof attrs === "object" ? attrs.imageUrl : null);
        case "youtube":
            return buildYouTubePreviewImageUrl(src, previewKey);
        case "pornHub":
            return buildProviderPlaceholderDataUrl("#f97316", "#431407", previewKey);
        case "redGifs":
            return buildProviderPlaceholderDataUrl("#ef4444", "#450a0a", previewKey);
        default:
            return null;
    }
}

export function extractPreviewImageUrlsFromTipTapContent(content: TiptapJsonNode | null | undefined): string[] {
    const orderedPreviewImageUrls: string[] = [];
    const seen = new Set<string>();
    let nodeIndex = 0;

    walkTiptapJson(content, (node) => {
        const previewImageUrl = buildPreviewImageUrl(node, `node-${nodeIndex}`);
        nodeIndex += 1;

        if (!previewImageUrl || seen.has(previewImageUrl)) {
            return;
        }

        seen.add(previewImageUrl);
        orderedPreviewImageUrls.push(previewImageUrl);
    });

    return orderedPreviewImageUrls;
}
